import React from "react";
import Menu from "@ui/Menu";
import { css } from "@linaria/core";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { ArrowRightDoubleLine } from "@ui/IconV2/react";
import TooltipsV2 from "@ui/TooltipV2/index";
import OptionList from "@ui/OptionList";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import { useNavigate, useParams } from "react-router";
import classNames from "classnames";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import t from "../../../../../i18n";
import { spacings } from "@ui/Theme";
import Tab from "@ui/NavV2/Tab";
import { color } from "@ui";
import { type Dashboard } from "../../../Dashboards/hook/api/dashboard";
import { isPlantStateTab, SUMMARY_ID } from "../utils";
import { toPath } from "../../../../../router/helpers/path";

const HIDDEN_SEE_MORE = "hidden-see-more";

interface DashboardTab extends Dashboard {
  visibility: boolean;
}

const SeeMoreMenu = ({
  projectId,
  visibleTabs,
}: {
  projectId: string;
  visibleTabs: DashboardTab[];
}) => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const [menuOpened, setMenuOpened] = React.useState(false);

  const dashboardToShowInToSeeMore = React.useMemo(
    () => visibleTabs.filter(f => f.visibility === false),
    [visibleTabs],
  );

  return (
    <Menu
      opened={menuOpened}
      setOpened={setMenuOpened}
      className={menu}
      overlay={
        <OptionList onRequestClose={() => {}}>
          {dashboardToShowInToSeeMore.map(dashboard => (
            <OptionV2
              className={optionOption}
              key={dashboard.title}
              onClick={() => {
                const path =
                  dashboard.id === SUMMARY_ID
                    ? toPath("/projects/:projectId/summary", {
                        projectId,
                      })
                    : toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
                        projectId,
                        dashboardId: dashboard.id,
                      });
                void navigate(path);
              }}
              selected={
                (dashboard.id === SUMMARY_ID && location.pathname.includes("summary")) ||
                dashboardId === dashboard.id
              }
              disabled={dashboard.visibility === true}
              closeOnClick
            >
              <FragmentSlot slot="text">{dashboard.title}</FragmentSlot>
              {dashboard.title.length > 0 && (
                <FragmentSlot slot="description">{dashboard.description}</FragmentSlot>
              )}
            </OptionV2>
          ))}
        </OptionList>
      }
    >
      <div>
        <Tab
          key="button"
          data-section-id="button"
          className={classNames({
            active: dashboardToShowInToSeeMore.some(
              tab =>
                (tab.id === SUMMARY_ID && location.pathname.includes("summary")) ||
                isPlantStateTab(tab.id, location.pathname) ||
                dashboardId === tab.id,
            ),
          })}
        >
          <TooltipsV2
            placement="top"
            overlay={<OverlayWrapper text={t("summary.dashboard.tab.tooltip.see_more")} />}
          >
            <ButtonIconV2 className={styledAddCircleLineIcon}>
              <ArrowRightDoubleLine color={color.carbon800} size="m" />
            </ButtonIconV2>
          </TooltipsV2>
        </Tab>
      </div>
    </Menu>
  );
};

export default SeeMoreMenu;

//#region CSS
const menu = css`
  max-height: 370px;
`;

const optionOption = css`
  max-width: 300px;
  & > * {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    @supports (-webkit-line-clamp: 2) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: initial;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
`;

const styledAddCircleLineIcon = css`
  margin-left: ${spacings.xxs};

  &.${HIDDEN_SEE_MORE} {
    visibility: hidden;
  }
`;
//#endregion CSS
